// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { caseInsensitiveEnum } from '@/constants/case-insensitive-enum';
import { z } from 'zod';

// Mirrors `enums.BillingFrequency` on the BE. Wire form is uppercase;
// caseInsensitiveEnum guards against the lowercase storage form
// occasionally leaking through.
export const billingFrequencySchema = caseInsensitiveEnum([
  'ONCE',
  'DAILY',
  'WEEKLY',
  'BIWEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'SEMIANNUALLY',
  'ANNUALLY',
  'BIENNIALLY',
  'TRIENNIALLY',
  'QUADRENNIALLY',
  'QUINQUENNIALLY',
]);

export type BillingFrequency = z.infer<typeof billingFrequencySchema>;

const BILLING_FREQUENCY_LABELS: Record<BillingFrequency, string> = {
  DAILY: 'Daily',
  ONCE: 'Once',
  WEEKLY: 'Weekly',
  BIWEEKLY: 'Every two weeks',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMIANNUALLY: 'Semi-annually',
  ANNUALLY: 'Annually',
  BIENNIALLY: 'Every two years',
  TRIENNIALLY: 'Every three years',
  QUADRENNIALLY: 'Every four years',
  QUINQUENNIALLY: 'Every five years',
};

// Returns a human-readable label for the given billing frequency.
// Tolerates either casing on the input — callers occasionally pass
// the raw BE storage form before the schema has parsed it.
export function getLabelForBillingFrequency(billingFrequency: string) {
  const normalized = billingFrequency.toUpperCase();
  if (normalized in BILLING_FREQUENCY_LABELS) {
    return BILLING_FREQUENCY_LABELS[normalized as BillingFrequency];
  }
  return billingFrequency;
}
